/**
 * Ouverture et reouverture de la session du convive.
 *
 * Payer ferme la session (ADR 0010). Le convive qui veut commander a nouveau
 * n'a pas a rescanner physiquement le QR code de sa table : l'application a
 * conserve le jeton du QR et rouvre une session neuve a sa place. Une session
 * neuve, et non la reprise de l'ancienne : les commandes payees restent
 * separees des suivantes.
 */

import type { ResolveQrResponse, SessionCheckResponse } from '@scan2eat/shared';
import { QR_TOKEN_STORAGE_KEY, SESSION_TOKEN_HEADER, SESSION_TOKEN_STORAGE_KEY } from '@scan2eat/shared';

import { readStoredValue } from './storage.js';

import { saveDisplayContext } from './display-context.js';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

/** Lit une cle, en retombant sur son nom d'avant le renommage (ADR 0020). */
function read(key: string): string | null {
  return readStoredValue(key);
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* noop */
  }
}

/** Memorise le jeton du QR code resolu, pour pouvoir rouvrir une session. */
export function rememberQrToken(qrToken: string): void {
  write(QR_TOKEN_STORAGE_KEY, qrToken);
}

/** Erreur de resolution, portant le code de l'API pour le message affiche. */
export class QrResolutionError extends Error {
  constructor(public readonly code: string) {
    super(code);
    this.name = 'QrResolutionError';
  }
}

/**
 * Resolutions en cours, par jeton de QR code.
 *
 * Resoudre un QR code sans presenter de jeton de session **cree un convive** :
 * c'est la regle, et le serveur a raison de l'appliquer -- deux personnes qui
 * scannent en meme temps sont deux convives.
 *
 * Mais deux appels partis du **meme navigateur** avant que le premier n'ait
 * range son jeton comptent alors pour deux. Cela arrive : le StrictMode de
 * React execute deux fois les effets en developpement, et un double appui ou
 * un rechargement impatient produisent la meme chose.
 *
 * Les appels concurrents partagent donc une seule promesse. Le suivant, une
 * fois le jeton range, presentera ce jeton et prolongera la session au lieu
 * d'en ouvrir une.
 */
const pendingResolutions = new Map<string, Promise<ResolveQrResponse>>();

async function requestResolve(qrToken: string): Promise<ResolveQrResponse> {
  // Le jeton en place est presente volontairement : il permet au serveur de
  // prolonger la session si elle est ouverte sur cette table, ou de chainer la
  // nouvelle a la precedente si elle est close.
  const token = read(SESSION_TOKEN_STORAGE_KEY);
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers[SESSION_TOKEN_HEADER] = token;

  const res = await fetch(`${BASE_URL}/public/tables/${encodeURIComponent(qrToken)}`, {
    headers,
    credentials: 'include',
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: { code?: string } }
      | null;
    throw new QrResolutionError(body?.error?.code ?? 'INTERNAL_ERROR');
  }

  const data = (await res.json()) as ResolveQrResponse;
  write(SESSION_TOKEN_STORAGE_KEY, data.sessionToken);
  rememberQrToken(qrToken);
  saveDisplayContext({
    restaurant: data.restaurant,
    table: { label: data.table.label, zone: data.table.zone },
  });
  return data;
}

/**
 * Resout un QR code, en garantissant un seul appel reseau a la fois.
 *
 * Deux invocations concurrentes rendent la meme promesse, donc le meme convive.
 */
export function resolveQrOnce(qrToken: string): Promise<ResolveQrResponse> {
  const existing = pendingResolutions.get(qrToken);
  if (existing) return existing;

  const promise = requestResolve(qrToken).finally(() => {
    pendingResolutions.delete(qrToken);
  });
  pendingResolutions.set(qrToken, promise);
  return promise;
}

/**
 * Ouvre une session a partir du QR code memorise.
 *
 * Requete brute plutot que `apiFetch` : ce dernier redirige vers l'ecran
 * « rescannez le QR code » des qu'il rencontre SESSION_EXPIRED, ce qui est
 * precisement ce qu'on cherche a eviter ici.
 */
async function reopenFromQr(): Promise<boolean> {
  const qrToken = read(QR_TOKEN_STORAGE_KEY);
  if (!qrToken) return false;

  try {
    await resolveQrOnce(qrToken);
    return true;
  } catch {
    return false;
  }
}

/**
 * Garantit une session ouverte, seule capable de porter une commande.
 *
 * Rend `rescan` quand aucune session ne peut etre ouverte : QR code inconnu,
 * table hors service, ou premier acces sans scan.
 */
export async function ensureOrderableSession(): Promise<'ready' | 'rescan'> {
  const token = read(SESSION_TOKEN_STORAGE_KEY);

  if (token) {
    try {
      const res = await fetch(`${BASE_URL}/public/session`, {
        headers: { Accept: 'application/json', [SESSION_TOKEN_HEADER]: token },
        credentials: 'include',
      });
      if (res.ok) {
        const data = (await res.json()) as SessionCheckResponse;
        // Rafraichir le contexte d'affichage ici, et pas seulement a la
        // resolution du QR code : il porte desormais de quoi annoncer les
        // frais de paiement, et un convive qui revient sur l'application sans
        // rescanner se verrait sinon proposer un montant sans son supplement.
        saveDisplayContext({
          restaurant: data.restaurant,
          table: { label: data.table.label, zone: data.table.zone },
        });
        if (data.session.status === 'OPEN') return 'ready';
      }
    } catch {
      /* Session inexploitable : on tente la reouverture. */
    }
  }

  return (await reopenFromQr()) ? 'ready' : 'rescan';
}
